import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MailIcon from "@mui/icons-material/Mail";
import { useContext, useEffect, useRef, useState } from "react";
import { DrawerContext } from "./Layout";
import Collapse from "@mui/material/Collapse";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { ConstantAPISContext } from "../../../context/ConstantAPISContext";
import { useNavigate } from "react-router";
import { formatTurkishToEnglishString } from "../../../helpers/TodayDate";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useLocation } from "react-router-dom";
import { EBELEDIYE_ICON, EBELEDIYE_LOGO } from "../../../config/environment";

type RetryImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  maxRetries?: number;
  retryDelayMs?: number;
};

const RetryImage: React.FC<RetryImageProps> = ({
  src,
  maxRetries = 2,
  retryDelayMs = 800,
  ...imgProps
}) => {
  const [attempt, setAttempt] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setCurrentSrc(src);
    setAttempt(0);
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [src]);

  const handleError = () => {
    if (attempt >= maxRetries) {
      return;
    }
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);

    timeoutRef.current = window.setTimeout(() => {
      const separator = src.includes("?") ? "&" : "?";
      setCurrentSrc(`${src}${separator}cb=${Date.now()}`);
    }, retryDelayMs);
  };

  return (
    <img
      loading="lazy"
      decoding="async"
      src={currentSrc}
      onError={handleError}
      {...imgProps}
    />
  );
};

const drawerWidth = 320;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc((${theme.spacing(7)} + 1px) * 1.1)`, // Kapalı genişliği mevcut değerin %10 fazlası
  [theme.breakpoints.up("sm")]: {
    width: `calc((${theme.spacing(8)} + 1px) * 1.1)`, // Büyük ekranlarda da kapalı genişliği %10 fazla
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

function delayAndExecute(callback: () => void, delayMs: number = 4000): void {
  setTimeout(() => {
    callback();
  }, delayMs);
}
const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const MiniDrawer = (props: any) => {
  const theme = useTheme();
  const { isOpenDrawer, setIsOpenDrawer } = useContext(DrawerContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // rota değişince drawer kapansın (sadece mobilde)
    if (isMobile) {
      setIsOpenDrawer(false);
    }
  }, [location.key]);

  useEffect(() => {
    function handleResize() {
      const isNowMobile = window.innerWidth <= 768;
      setIsMobile(isNowMobile);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile && !isOpenDrawer) {
      setIsOpenDrawer(true);
    }
  };
  const [loading, setLoading] = useState(false);

  const handleMouseLeave = () => {
    if (!isMobile && isOpenDrawer) {
      setIsOpenDrawer(false);
    }
  };

  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);
  const handleToggleSubMenu = (index: number) => {
    setOpenSubMenu(openSubMenu === index ? null : index);
  };

  const [selectedItem, setSelectedItem] = useState(""); // Hangi öğenin seçildiğini izlemek için
  const { menuList } = useContext(ConstantAPISContext);

  const handleMenuItemClick = (menuAdi: string) => {
    setSelectedItem(menuAdi);
  };
  if(loading){
    return <div style={{backgroundColor:"#ffffff",height:"100%",zIndex:"1000",position:"absolute",top:0,left:0,right:0,bottom:0}} className="d-flex w-100 flex-column justify-content-center align-items-center">
      <CircularProgress></CircularProgress>
      <Typography className="mt-3">Ödeme sayfasına yönlendiriliyorsunuz, ekranın yönlendireceği kimlik doğrulama işlemlerini takip etmelisiniz.</Typography>
    </div>
  }
  return (
    <Box
      sx={{ display: "flex" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Drawer
        className="shadow"
        anchor="left"
        variant={isMobile ? "temporary" : "permanent"}
        open={isOpenDrawer}
        onClose={() => setIsOpenDrawer(false)}
      >
        <DrawerHeader
          onClick={() => navigate("/")}
          style={{ paddingRight: "0.4rem", height: "72px", cursor: "pointer", position: "relative" }}
        >
          {/* Büyük logo (Drawer açıkken) */}
          <Box
            sx={{
              marginLeft:"0.2rem",
              position: "relative",
              width: "100%",
              height: "50px",
              overflow: "hidden",
              pl: "0.8rem",
            }}
          >
            <RetryImage
              src={(EBELEDIYE_LOGO as String).toString()}
              alt="Logo"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "left center",
                opacity: isOpenDrawer ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
                pointerEvents: "none",
              }}
            />

            {/* Küçük logo (Drawer kapalıyken) */}
            <RetryImage
              src={(EBELEDIYE_ICON as String).toString()}
              alt="Küçük Logo"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "left center",
                opacity: isOpenDrawer ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
                pointerEvents: "none",
              }}
            />
          </Box>
        </DrawerHeader>

        <Divider />
        <List>
          {menuList.map((item, index) =>
            item.altBasliklar.length > 0 ? (
              <React.Fragment key={index}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedItem === item.ustBaslik.menuAdi} // Seçili öğenin belirlenmesi
                    onClick={() => {
                      handleMenuItemClick(item.ustBaslik.menuAdi);
                      handleToggleSubMenu(index);
                      if (item.sayfaLink == null) {
                        // handle navigation when sayfaLink is null
                      } else if (item.sayfaLink.startsWith("http")) {
                        window.location.href = item.sayfaLink;
                      } else {
                        navigate(item.sayfaLink);
                      }
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.contrastText,
                        },
                      },
                      ...(selectedItem === item.ustBaslik.menuAdi && {
                        borderRight: `4px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.2)`,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.light,
                        },
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ paddingLeft: "0.45rem" }}>
                      <i
                        style={{ fontSize: "1.2rem" }}
                        className={item.ustBaslik.ikonYolu2}
                      ></i>
                    </ListItemIcon>
                    <ListItemText primary={item.ustBaslik.menuAdi}/>
                    {openSubMenu === index ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={isOpenDrawer && openSubMenu === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.altBasliklar.map((subItem: any, subIndex: number) => (
                      <ListItemButton
                        onClick={() => {
                          if (subItem.sayfaLink == null) {
                            navigate(
                              `/anasayfa/${formatTurkishToEnglishString(
                                subItem.title.replaceAll(" ", "-")
                              )}`,
                              {
                                state: { altBasliklar: subItem.altBasliklar },
                              }
                            );
                          } else if (subItem.sayfaLink.startsWith("http")) {
                            console.log("subItem", subItem);
                            if (subItem.sqId === 2) {
                              console.log("subItem", subItem);
                              setLoading(true);
                              delayAndExecute(() => {
                                setLoading(false); // İstersen bunu da kaldırabilirsin çünkü zaten yönlendiriliyor
                                window.location.href = subItem.sayfaLink;
                              });
                            } else {
                              window.location.href = subItem.sayfaLink;
                            }
                          } else {
                            navigate(subItem.sayfaLink);
                          }
                        }}
                        key={subIndex}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                            "& .MuiListItemIcon-root": {
                              color: theme.palette.primary.contrastText,
                            },
                          },
                          ...(selectedItem === subItem.menuAdi && {
                            borderRight: `4px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 4px 8px rgba(0, 0, 0, 0.2)`,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.light,
                            },
                          }),
                        }}
                      >
                        <ListItemIcon sx={{ paddingLeft: "0.45rem" }}>
                          <i
                            style={{ fontSize: "1.2rem" }}
                            className={subItem.ikonYolu2}
                          ></i>
                        </ListItemIcon>
                        <ListItemText primary={subItem.menuAdi} />
                      </ListItemButton>
                    ))}
                    <hr />
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
              <ListItem
                disablePadding
                key={index} // key eksikti, ekledim
                onClick={() => {
                  if (item.ustBaslik.sayfaLink.startsWith("http")) {
                    window.location.href = item.ustBaslik.sayfaLink;
                  } else {
                    navigate(item.ustBaslik.sayfaLink);
                  }
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => handleMenuItemClick(item.ustBaslik.menuAdi)}
                  selected={selectedItem === item.ustBaslik.menuAdi} // Seçili öğenin belirlenmesi
                  sx={{
                    ...(selectedItem === item.ustBaslik.menuAdi && {
                      borderRight: `4px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 8px rgba(0, 0, 0, 0.2)`,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                      },
                    }),
                  }}
                >
                  <ListItemIcon sx={{ paddingLeft: "0.45rem" }}>
                    <i
                      style={{ fontSize: "1.2rem" }}
                      className={item.ustBaslik.ikonYolu2}
                    ></i>
                  </ListItemIcon>
                  <ListItemText primary={item.ustBaslik.menuAdi} />
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default MiniDrawer;
